import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useBrands } from "../utils/useBrands";
import { computeReadyToRunStats, formatBrandSearchLabel } from "../utils/helpersOfBrands";
import AllBrandsFilterCard from "./AllBrandsFilterCard";
import AllBrandsTable from "./AllBrandsTable";
import BrandsSkeleton from "../BrandsSkeleton";

// Tab entry for "All Brands" — every row from the query response (is_all=1).
const AllBrands = () => {
  const { allBrandsData, isLoadingBrandsData } = useBrands();

  const rows = useMemo(
    () => (allBrandsData || []).filter((row: any) => row?.is_all === 1),
    [allBrandsData],
  );

  const [selectedBrandNames, setSelectedBrandNames] = useState<string[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string[] | null>(null);
  const [selectedAffiliateSystem, setSelectedAffiliateSystem] = useState<string[] | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<string[] | null>(null);
  const [selectedWebsite, setSelectedWebsite] = useState<string[] | null>(null);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const filteredRows = useMemo(() => {
    return rows.filter((row: any) => {
      const brandNameMatch =
        selectedBrandNames.length === 0 ||
        selectedBrandNames.includes(formatBrandSearchLabel(row));
      const statusMatch = !selectedStatus?.length || selectedStatus.includes(row.status);
      const affiliateSystemMatch =
        !selectedAffiliateSystem?.length ||
        selectedAffiliateSystem.includes(row.affiliate_system);
      const companyMatch =
        !selectedCompany?.length || selectedCompany.includes(row.company_name);
      const websiteMatch =
        !selectedWebsite?.length || selectedWebsite.includes(row.website_name);
      return (
        brandNameMatch &&
        statusMatch &&
        affiliateSystemMatch &&
        companyMatch &&
        websiteMatch
      );
    });
  }, [
    rows,
    selectedBrandNames,
    selectedStatus,
    selectedAffiliateSystem,
    selectedCompany,
    selectedWebsite,
  ]);

  // Reference line is computed against every row on the tab (Y = all rows),
  // not the filtered subset, per spec.
  const stats = computeReadyToRunStats(rows);

  // Selection should always reflect the currently displayed/search-filtered
  // rows — drop any selected brand that no longer matches the active
  // search/filters (including clearing everything when nothing matches).
  useEffect(() => {
    const visibleIds = new Set(filteredRows.map((row: any) => row.brand_id));
    setSelectedRows((prev) => {
      const next = prev.filter((id) => visibleIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [filteredRows]);

  // Removing/clearing a filter widens the visible rows again, so the
  // intersection-based pruning above would otherwise just leave the old
  // selection untouched. Per spec, clearing any filter (individually or via
  // "Clear Filters") should wipe the selection entirely instead of carrying
  // it forward — runs after the effect above so it takes precedence.
  const prevFiltersRef = useRef({
    selectedBrandNames,
    selectedStatus,
    selectedAffiliateSystem,
    selectedCompany,
    selectedWebsite,
  });
  useEffect(() => {
    const prev = prevFiltersRef.current;
    const wasActive = (val: string[] | null) => !!val && val.length > 0;
    const isEmpty = (val: string[] | null) => !val || val.length === 0;

    const aFilterWasCleared =
      (wasActive(prev.selectedBrandNames) && isEmpty(selectedBrandNames)) ||
      (wasActive(prev.selectedStatus) && isEmpty(selectedStatus)) ||
      (wasActive(prev.selectedAffiliateSystem) && isEmpty(selectedAffiliateSystem)) ||
      (wasActive(prev.selectedCompany) && isEmpty(selectedCompany)) ||
      (wasActive(prev.selectedWebsite) && isEmpty(selectedWebsite));

    if (aFilterWasCleared) {
      setSelectedRows([]);
    }

    prevFiltersRef.current = {
      selectedBrandNames,
      selectedStatus,
      selectedAffiliateSystem,
      selectedCompany,
      selectedWebsite,
    };
  }, [
    selectedBrandNames,
    selectedStatus,
    selectedAffiliateSystem,
    selectedCompany,
    selectedWebsite,
  ]);

  const clearFilters = () => {
    setSelectedBrandNames([]);
    setSelectedStatus(null);
    setSelectedAffiliateSystem(null);
    setSelectedCompany(null);
    setSelectedWebsite(null);
  };

  if (isLoadingBrandsData) {
    return <BrandsSkeleton />;
  }

  return (
    <Box>
      <AllBrandsFilterCard
        rows={rows}
        selectedRows={selectedRows}
        selectedBrandNames={selectedBrandNames}
        setSelectedBrandNames={setSelectedBrandNames}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        selectedAffiliateSystem={selectedAffiliateSystem}
        setSelectedAffiliateSystem={setSelectedAffiliateSystem}
        selectedCompany={selectedCompany}
        setSelectedCompany={setSelectedCompany}
        selectedWebsite={selectedWebsite}
        setSelectedWebsite={setSelectedWebsite}
        clearFilters={clearFilters}
      />

      <Typography
        variant="body2"
        sx={{
          color: "#0288D1",
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          my: 1,
        }}
      >
        <InfoOutlinedIcon fontSize="inherit" />
        Please note: {stats.ready} of {stats.total} brands are ready to run (
        {stats.percent}%)
      </Typography>

      <AllBrandsTable
        rows={filteredRows}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
      />
    </Box>
  );
};

export default AllBrands;
